import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  AdminApprovalPayload,
  ContactMethod,
  ConnectionStatus,
  Connection,
  ConnectionNotification,
  PresenceChanged,
  PresenceSnapshot,
  ProfileSnapshotPayload,
  PrivacySettings,
  NotificationSettings,
  SubscriptionPlan,
  SubscriptionTier,
  ConnectLimitResetPayload,
  UserProfile
} from '../types';
import {
  login as apiLogin,
  register as apiRegister,
  refresh as refreshSession,
  revoke as revokeSession,
  getProfile,
  updateProfile,
  uploadProfilePhotos,
  reorderProfilePhotos,
  updateLocation,
  getConnectionCandidates,
  getConnectiones,
  getConnectQueue,
  submitConnectAction,
  deleteConnection,
  getConnectLimitStatus,
  approvePendingUser,
  getPendingUsers,
  rejectPendingUser,
  deleteProfilePhoto,
  deleteProfile,
  pauseProfile as apiPauseProfile,
  resumeProfile as apiResumeProfile,
  upsertRegistrationDraft as apiUpsertRegistrationDraft,
  finalizeRegistrationDraft as apiFinalizeRegistrationDraft,
  updatePreferredLanguage
} from '../lib/apiClient';
import { GENDER_OPTIONS, LOOKING_FOR_OPTIONS, PRONOUN_OPTIONS, SEXUALITY_OPTIONS } from '../constants/filterOptions';
import { setStoredAccessToken, clearStoredAccessToken } from '../lib/tokenStorage';
import { API_BASE_URL } from '../lib/httpClient';
import { canUseDiscover } from '../lib/profileTypes';
import type {
  AuthenticatedUserDto,
  AuthTokensResponseDto,
  ProfileDetailsDto,
  ConnectionCandidateDto,
  ConnectionListItemDto,
  ConnectQueueProfileDto,
  ConnectRequestDto,
  ConnectLimitStatusDto,
  ApiErrorDetails,
  UpdateProfileRequestDto
} from '../lib/apiClient';
import type { SupportedLanguage } from '../types/i18n';
import { getPreferredVenueAddress } from '../lib/venue';

type OnlineUserState = {
  isOnline: boolean;
  lastSeenUtc?: string;
};

type AuthActionResult = { success: true } | { success: false; error: string; code?: string; status?: number; details?: ApiErrorDetails };

type RegisterPayload = {
  email: string;
  password: string;
  firstName: string;
  dateOfBirth: string;
  gender: string;
  sexuality: string;
  pronouns?: string | null;
  lookingFor: string;
  latitude: number;
  longitude: number;
  wantsTestUser?: boolean;
  locationLabel?: string | null;
  altchaToken?: string | null;
  marketingOptIn?: boolean;
  approvalEmailsOptIn?: boolean;
  connectionEmailsOptIn?: boolean;
  preferredLanguage?: SupportedLanguage;
};

type RegistrationDraftPayload = RegisterPayload & {
  draftId?: string | null;
  emailBypassed?: boolean;
  emailConfirmed?: boolean;
  metadataJson?: string | null;
};

type DraftActionResult =
  | { success: true; draftId: string; emailConfirmed: boolean; status?: string | null }
  | { success: false; error: string; code?: string; status?: number };

type SaveProfilePayload = Pick<UpdateProfileRequestDto, 'aboutMe' | 'firstName' | 'gender' | 'sexuality'> & {
  lookingFor?: string | string[] | null;
  location?: string;
  venue?: UpdateProfileRequestDto['venue'];
  age?: number;
  newEvent?: boolean | null;
  eventDate?: string | null;
  eventTime?: string | null;
  profileLink?: string | null;
  socialPlatform?: string;
  socialHandle?: string;
  secondarySocialPlatform?: string;
  secondarySocialHandle?: string;
  pronouns?: string | null;
  marketingOptIn?: boolean;
  approvalEmailsOptIn?: boolean;
  connectionEmailsOptIn?: boolean;
  isVisibleToOthers?: boolean;
};

type LayoutMode = 'mobile' | 'desktop';

type SaveProfileResult = { success: boolean; message?: string };
type PhotoUploadResult = { success: boolean; error?: string; status?: number; code?: string };

interface AppState {
  user: UserProfile | null;
  discoverQueue: Connection[];
  matches: Connection[];
  unviewedConnectionCount: number;
  viewedConnectionKeysByUser: Record<string, string[]>;
  likedProfileIds: string[];
  dismissedProfileIds: string[];
  availablePlans: SubscriptionPlan[];
  pendingVerification: boolean;
  notifications: ConnectionNotification[];
  isLoadingConnectiones: boolean;
  isRefreshingQueue: boolean;
  connectionStatus: ConnectionStatus;
  onlineUsers: Record<string, OnlineUserState>;
  lastPresenceSync?: string;
  lastSyncedAt?: string;
  authToken: string | null;
  authInitialized: boolean;
  language: SupportedLanguage;
  layoutMode: LayoutMode;
  queueRefreshReason: string | null;
  matchCelebration: Connection | null;
  connectLimit: ConnectLimitState | null;
  isConnectLimitModalOpen: boolean;
  setUser: (user: UserProfile) => void;
  restoreDefaultUser: () => void;
  updateUser: (update: Partial<UserProfile>) => void;
  detectAndUpdateLocation: () => Promise<{ success: boolean; error?: string; errorCode?: string; label?: string }>;
  saveProfile: (payload: SaveProfilePayload) => Promise<SaveProfileResult>;
  addPhoto: (photoUrl: string) => void;
  removePhoto: (photoUrl: string) => void;
  uploadProfilePhotos: (files: File[]) => Promise<PhotoUploadResult>;
  reorderProfilePhotos: (photoIds: string[]) => Promise<PhotoUploadResult>;
  deleteProfilePhoto: (photoId: string) => Promise<boolean>;
  refreshUserProfile: () => Promise<boolean>;
  recordConnectionDecision: (id: string, decision: 'like' | 'pass') => Promise<void>;
  markConnectionAsViewed: (id: string) => void;
  setSubscriptionTier: (tier: SubscriptionTier) => void;
  setPendingVerification: (pending: boolean) => void;
  dismissNotification: (id: string) => void;
  pushNotification: (notification: ConnectionNotification) => void;
  clearNotifications: () => void;
  setIsLoadingConnectiones: (pending: boolean) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setOnlineUsers: (snapshot: PresenceSnapshot) => void;
  updatePresence: (change: PresenceChanged) => void;
  applyProfileSnapshot: (snapshot: ProfileSnapshotPayload) => void;
  applyAdminApprovalUpdate: (update: AdminApprovalPayload) => void;
  handleConnectLimitExceeded: (details?: ApiErrorDetails | null) => void;
  applyConnectLimitReset: (payload: ConnectLimitResetPayload) => void;
  setAuthToken: (token: string | null) => void;
  setLanguage: (language: SupportedLanguage) => void;
  setLayoutMode: (mode: LayoutMode) => void;
  triggerConnectionCelebration: (match: Connection) => void;
  dismissConnectionCelebration: () => void;
  refreshConnectQueue: (options?: ConnectQueueRefreshOptions) => Promise<void>;
  refreshConnectLimit: () => Promise<void>;
  loadConnectiones: (options?: ConnectionLoadOptions) => Promise<string | null>;
  unmatch: (matchId: string) => Promise<boolean>;
  authenticate: (email: string, password: string, rememberMe?: boolean) => Promise<AuthActionResult>;
  registerAccount: (payload: RegisterPayload) => Promise<AuthActionResult>;
  upsertRegistrationDraft: (payload: RegistrationDraftPayload) => Promise<DraftActionResult>;
  finalizeRegistration: (draftId: string) => Promise<AuthActionResult>;
  initializeAuth: () => Promise<void>;
  pauseAccount: () => Promise<{ success: boolean; error?: string; code?: string; status?: number }>;
  resumeAccount: () => Promise<{ success: boolean; error?: string; code?: string; status?: number }>;
  deleteAccount: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  setConnectLimitModalOpen: (open: boolean) => void;
}

const baseUserTemplate: UserProfile = {
  id: 'user-001',
  displayName: 'Alex Rivera',
  email: 'alex.rivera@example.com',
  age: 29,
  location: 'San Francisco, CA',
  bio: 'Product designer who loves hiking, modern art, and discovering coffee shops with character.',
  photos: [
    'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=600&q=80',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=600&q=80',
    'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?auto=format&fit=crop&w=600&q=80'
  ],
  socialHandles: { platform: null, handle: null, signal: null, secondaryPlatform: null, secondaryHandle: null },
  verified: true,
  subscription: 'plus',
  subscriptionStatus: 'active',
  subscriptionPeriodEndUtc: null,
  privacy: {
    incognitoMode: false,
    showOnlineStatus: true,
    shareLocation: true,
    dataSharingConsent: true
  },
  notifications: {
    email: true,
    push: true,
    sms: false
  },
  marketingOptIn: false,
  approvalEmailsOptIn: false,
  connectionEmailsOptIn: false,
  isVisibleToOthers: true,
  isActive: true,
  isPaused: false,
  isApproved: true,
  isAdmin: false,
  hasPendingProfileChanges: false,
  pendingProfileSubmittedAtUtc: null,
  updatedAtUtc: new Date().toISOString()
};

const baseDiscoverProfiles: Connection[] = [
  {
    id: 'match-001',
    name: 'Mia Chen',
    age: 27,
    bio: 'Adventure guide and foodie. Looking for someone to hike, cook, and laugh with.',
    compatibility: 92,
    image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=600&q=80',
    distanceKm: 5,
    lastActive: '2m ago',
    isNew: true,
    isOnline: true,
    lastSeenUtc: new Date().toISOString(),
    photos: [
      {
        src: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=900&q=80',
        alt: 'Mia smiling outdoors in nature'
      },
      {
        src: 'https://images.unsplash.com/photo-1502720705749-3c03b66e4b40?auto=format&fit=crop&w=900&q=80',
        alt: 'Mia preparing food in a market'
      },
      {
        src: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=900&q=80',
        alt: 'Mia climbing an indoor rock wall'
      }
    ],
    contactMethods: [
      { type: 'social', label: 'Social', handle: '@mia.trails' },
      { type: 'signal', label: 'Signal', handle: '@mia-chen' }
    ]
  },
  {
    id: 'match-002',
    name: 'Jordan Lee',
    age: 31,
    bio: 'Engineer by day, DJ by night. Passionate about music, tech, and good stories.',
    compatibility: 88,
    image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=600&q=80',
    distanceKm: 12,
    lastActive: '10m ago',
    isNew: false,
    isOnline: false,
    photos: [
      {
        src: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=900&q=80',
        alt: 'Jordan performing DJ set'
      },
      {
        src: 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=900&q=80',
        alt: 'Jordan working on a laptop with headphones'
      }
    ],
    contactMethods: [
      { type: 'social', label: 'Social', handle: '@loopwithjordan' },
      { type: 'signal', label: 'Signal', handle: '@jordan-lee' }
    ]
  },
  {
    id: 'match-003',
    name: 'Sasha Patel',
    age: 28,
    bio: 'Sustainability advocate and amateur photographer. Weekend farmer’s market regular.',
    compatibility: 84,
    image: 'https://images.unsplash.com/photo-1463453091185-61582044d556?auto=format&fit=crop&w=600&q=80',
    distanceKm: 3,
    lastActive: '1h ago',
    isNew: true,
    isOnline: false,
    photos: [
      {
        src: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=80',
        alt: 'Sasha holding a vintage camera'
      },
      {
        src: 'https://images.unsplash.com/photo-1484291470158-b8f8d608850d?auto=format&fit=crop&w=900&q=80',
        alt: 'Sasha practicing yoga outdoors'
      },
      {
        src: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=900&q=80',
        alt: 'Sasha exploring a forest trail'
      }
    ],
    contactMethods: [
      { type: 'social', label: 'Social', handle: '@sasha.seeds' }
    ]
  }
];

const cloneUser = (template: UserProfile): UserProfile => ({
  ...template,
  lookingFor: template.lookingFor ? [...template.lookingFor] : undefined,
  photos: [...template.photos],
  socialHandles: template.socialHandles ? { ...template.socialHandles } : undefined,
  privacy: { ...template.privacy },
  notifications: { ...template.notifications },
  hasPendingProfileChanges: template.hasPendingProfileChanges,
  pendingProfileSubmittedAtUtc: template.pendingProfileSubmittedAtUtc
});

const cloneConnection = (template: Connection): Connection => ({
  ...template,
  lookingFor: template.lookingFor ? [...template.lookingFor] : undefined,
  photos: template.photos ? template.photos.map((photo) => ({ ...photo })) : undefined,
  contactMethods: template.contactMethods ? template.contactMethods.map((method) => ({ ...method })) : undefined
});

const cloneConnectiones = (templates: Connection[]): Connection[] => templates.map((match) => cloneConnection(match));

// Normalize API origin so photo URLs resolve correctly whether the base ends with /api or /api/.
const API_ORIGIN = (() => {
  const trimmed = API_BASE_URL.replace(/\/+$/, '');
  return trimmed.replace(/\/api$/i, '');
})();

const normalizePhotoUrl = (url?: string | null): string | null => {
  if (!url) {
    return null;
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const sanitized = url.replace(/^\/+/, '');
  return `${API_ORIGIN}/${sanitized}`;
};

const avatarUrlForCandidate = (id: string, name: string): string => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);

  const fallbackInitials = initials || 'EN';
  return `https://avatar.vercel.sh/${encodeURIComponent(id)}.svg?text=${encodeURIComponent(fallbackInitials)}`;
};

const mapCandidateToConnection = (candidate: ConnectionCandidateDto): Connection => {
  const name = candidate.firstName?.trim() || 'Member';
  const compatibilityScore = 75;

  return {
    id: candidate.userId,
    name,
    profileType: candidate.profileType ?? 'Standard',
    eventDate: candidate.eventDate ?? null,
    eventTime: candidate.eventTime ?? null,
    age: candidate.age,
    bio: candidate.about ?? '',
    gender: candidate.gender,
    sexuality: candidate.sexuality,
    pronouns: candidate.pronouns ?? null,
    lookingFor: normalizeLookingFor(candidate.lookingFor),
    compatibility: compatibilityScore,
    image: avatarUrlForCandidate(candidate.userId, name),
    distanceKm: Number.isFinite(candidate.distanceKm) ? candidate.distanceKm : 0,
    lastActive: 'Recently active',
    isNew: true,
    contactMethods: []
  };
};

const normalizeSocialLabel = (platform?: string | null): string => {
  if (!platform) {
    return 'Social';
  }

  const value = platform.trim();
  const normalized = value.toLowerCase();

  switch (normalized) {
    case 'signal':
      return 'Signal';
    case 'snapchat':
      return 'Snapchat';
    case 'discord':
      return 'Discord';
    case 'whatsapp':
      return 'WhatsApp';
    default:
      return value || 'Social';
  }
};

const addContactMethod = (contactMethods: ContactMethod[], platform?: string | null, handle?: string | null) => {
  const cleanedHandle = handle?.trim();
  if (!cleanedHandle) {
    return;
  }

  const label = normalizeSocialLabel(platform);
  const handleKey = cleanedHandle.toLowerCase();
  const typeKey = label.toLowerCase();

  const exists = contactMethods.some(
    (method) => method.handle.toLowerCase() === handleKey && method.type === typeKey
  );
  if (exists) {
    return;
  }

  contactMethods.push({ type: typeKey, label, handle: cleanedHandle });
};

const buildSocialLabels = (
  primaryPlatform?: string | null,
  secondaryPlatform?: string | null,
  signalHandle?: string | null
): string[] => {
  const labels = [primaryPlatform, secondaryPlatform]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .map((value) => normalizeSocialLabel(value));

  if (!labels.some((value) => value.toLowerCase() === 'signal') && signalHandle?.trim()) {
    labels.push('Signal');
  }

  return labels.filter((value, index) => labels.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index);
};

const normalizeLookingFor = (value?: string | string[] | null | unknown): string[] => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((v) => (v == null ? '' : String(v).trim()))
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(/[,;]+/)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  return [];
};


const mapConnectProfileToConnection = (profile: ConnectQueueProfileDto): Connection => {
  const name = profile.firstName?.trim() || 'Member';
  const compatibilityScore = Math.max(40, Math.min(99, 96 - Math.round(profile.distanceKm ?? 0)));
  const gallery = Array.isArray(profile.photos)
    ? profile.photos
        .map((url, index) => {
          const resolved = normalizePhotoUrl(url);
          return resolved ? { src: resolved, alt: `${name}'s photo ${index + 1}` } : null;
        })
        .filter((photo): photo is { src: string; alt: string } => Boolean(photo))
    : [];

  const socialLabels = buildSocialLabels(profile.socialHandles?.platform, profile.socialHandles?.secondaryPlatform);

  return {
    id: profile.userId,
    name,
    profileType: profile.profileType ?? 'Standard',
    eventDate: profile.eventDate ?? null,
    eventTime: profile.eventTime ?? null,
    profileLink: profile.profileLink ?? null,
    venue: profile.venue ?? null,
    age: profile.age,
    bio: profile.about ?? '',
    gender: profile.gender,
    sexuality: profile.sexuality,
    pronouns: profile.pronouns ?? null,
    lookingFor: normalizeLookingFor(profile.lookingFor),
    compatibility: compatibilityScore,
    image: gallery[0]?.src ?? avatarUrlForCandidate(profile.userId, name),
    distanceKm: Number.isFinite(profile.distanceKm) ? profile.distanceKm : 0,
    lastActive: 'Recently active',
    isNew: true,
    photos: gallery.length > 0 ? gallery : undefined,
    socialLabels: socialLabels.length > 0 ? socialLabels : undefined
  };
};

export const mapConnectionListItemToConnection = (item: ConnectionListItemDto): Connection => {
  const name = item.firstName?.trim() || 'Member';
  const isInactive = Boolean(item.isInactive);
  const gallery = Array.isArray(item.photos)
    ? item.photos
        .map((url, index) => {
          const resolved = normalizePhotoUrl(url);
          return resolved ? { src: resolved, alt: `${name}'s photo ${index + 1}` } : null;
        })
        .filter((photo): photo is { src: string; alt: string } => Boolean(photo))
    : [];

  const contactMethods: ContactMethod[] = [];
  if (!isInactive && item.socialHandles) {
    addContactMethod(contactMethods, item.socialHandles.platform, item.socialHandles.handle);
    addContactMethod(contactMethods, 'Signal', item.socialHandles.signal);
    addContactMethod(contactMethods, item.socialHandles.secondaryPlatform, item.socialHandles.secondaryHandle);
  }

  const socialLabels = buildSocialLabels(item.socialHandles?.platform, item.socialHandles?.secondaryPlatform, item.socialHandles?.signal);

  const photos = !isInactive && gallery.length > 0 ? gallery : undefined;

  return {
    id: item.partnerId,
    matchId: item.matchId ?? item.connectionId,
    name,
    profileType: item.profileType ?? 'Standard',
    eventDate: item.eventDate ?? null,
    eventTime: item.eventTime ?? null,
    profileLink: item.profileLink ?? null,
    venue: item.venue ?? null,
    age: item.age,
    bio: isInactive ? '' : item.about ?? '',
    gender: item.gender,
    sexuality: item.sexuality,
    pronouns: item.pronouns ?? null,
    lookingFor: normalizeLookingFor(item.lookingFor),
    compatibility: 95,
    image: photos?.[0]?.src ?? avatarUrlForCandidate(item.partnerId, name),
    distanceKm: typeof item.distanceKm === 'number' ? item.distanceKm : 0,
    lastActive: 'Recently active',
    isNew: Boolean(item.isRecent),
    photos,
    contactMethods: contactMethods.length > 0 ? contactMethods : undefined,
    socialLabels: socialLabels.length > 0 ? socialLabels : undefined,
    matchedAtUtc: item.matchedAtUtc ?? item.connectedAtUtc ?? '',
    isInactive
  };
};

const defaultPrivacySettings: PrivacySettings = { ...baseUserTemplate.privacy };
const defaultNotificationSettings: NotificationSettings = { ...baseUserTemplate.notifications };
const DEFAULT_LOCATION = 'Location not set';

const mapApiUserProfile = (authUser: AuthenticatedUserDto, profile?: ProfileDetailsDto | null): UserProfile => {
  const pendingProfile = profile?.pendingRevision?.proposedProfile;
  const hasPendingProfileChanges = Boolean(profile?.pendingRevision?.hasProfileChanges);

  const photoUrls = profile?.photos?.length
    ? profile.photos
        .map((photo) => normalizePhotoUrl(photo.url))
        .filter((url): url is string => Boolean(url))
    : [];

  const photoRefs = (profile?.photos ?? [])
    .map((photo) => ({
      id: photo.id,
      url: normalizePhotoUrl(photo.url) ?? photo.url,
      isApproved: photo.isApproved,
      isPendingReview: photo.isPendingReview,
      reviewError: photo.reviewError ?? null,
      uploadedAtUtc: photo.uploadedAtUtc,
      displayOrder: photo.displayOrder
    }))
    .filter((p) => Boolean(p.url));

  const isTestUser = profile?.isTestUser ?? authUser.isTestUser;
  const subscriptionStatus = profile?.subscriptionStatus ?? 'none';
  const derivedSubscription = isTestUser || profile?.hasSubscription ? 'plus' : 'free';

  const effectiveSocial = pendingProfile?.social ?? profile?.social;
  const effectiveLookingFor = pendingProfile
    ? pendingProfile.lookingFor
    : profile
      ? profile.lookingFor
      : authUser.lookingFor;
  const effectiveGender = pendingProfile
    ? pendingProfile.gender
    : profile
      ? profile.gender
      : authUser.gender;
  const effectiveSexuality = pendingProfile
    ? pendingProfile.sexuality
    : profile
      ? profile.sexuality
      : authUser.sexuality;

  return {
    id: authUser.id,
    displayName: pendingProfile?.firstName ?? profile?.firstName ?? authUser.firstName ?? 'Member',
    profileType: profile?.profileType ?? 'Standard',
    eventDate: profile?.eventDate ?? null,
    eventTime: profile?.eventTime ?? null,
    profileLink: pendingProfile?.profileLink ?? profile?.profileLink ?? null,
    venue: pendingProfile?.venue ?? profile?.venue ?? null,
    email: authUser.email || profile?.email || '',
    age: pendingProfile?.age ?? profile?.age ?? authUser.age ?? 0,
    location: getPreferredVenueAddress(pendingProfile?.venue ?? profile?.venue, pendingProfile?.location || profile?.location) || DEFAULT_LOCATION,
    bio: pendingProfile?.aboutMe ?? profile?.aboutMe ?? '',
    gender: effectiveGender,
    sexuality: effectiveSexuality,
    pronouns: toCanonicalPronoun(pendingProfile?.pronouns ?? profile?.pronouns ?? authUser.pronouns),
    lookingFor: toSafeLookingForList(effectiveLookingFor, authUser.lookingFor),
    photos: photoUrls,
    photoRefs,
    socialHandles: {
      platform: effectiveSocial?.platform ?? null,
      handle: effectiveSocial?.handle ?? null,
      signal: effectiveSocial?.signal ?? null,
      secondaryPlatform: effectiveSocial?.secondaryPlatform ?? null,
      secondaryHandle: effectiveSocial?.secondaryHandle ?? null
    },
    verified: Boolean(profile?.isApproved ?? authUser.isApproved ?? false),
    subscription: derivedSubscription,
    subscriptionStatus,
    subscriptionPeriodEndUtc: profile?.subscriptionCurrentPeriodEndUtc ?? null,
    privacy: { ...defaultPrivacySettings },
    notifications: { ...defaultNotificationSettings },
    marketingOptIn: profile?.marketingOptIn ?? false,
    approvalEmailsOptIn: profile?.approvalEmailsOptIn ?? false,
    connectionEmailsOptIn: profile?.connectionEmailsOptIn ?? false,
    isVisibleToOthers: profile?.isVisibleToOthers ?? false,
    isActive: profile?.isActive ?? authUser.isActive,
    isPaused: profile?.isPaused ?? authUser.isPaused,
    isApproved: profile?.isApproved ?? authUser.isApproved ?? undefined,
    isTestUser,
    isAdmin: authUser.isAdmin,
    hasPendingProfileChanges,
    pendingProfileSubmittedAtUtc: hasPendingProfileChanges ? profile?.pendingRevision?.requestedAtUtc ?? null : null,
    updatedAtUtc: profile?.updatedAtUtc ?? authUser.updatedAtUtc
  };
};

const derivePendingVerification = (profile?: ProfileDetailsDto | null, authUser?: AuthenticatedUserDto): boolean => {
  const approval = profile?.isApproved ?? authUser?.isApproved;
  return approval === false;
};

const defaultNotifications: ConnectionNotification[] = [];

const subscriptionPlans: SubscriptionPlan[] = [
  {
    tier: 'free',
    priceMonthly: 0,
    features: ['Daily connection limit', 'Basic filters', 'Read receipts']
  },
  {
    tier: 'plus',
    priceMonthly: 19,
    features: ['Unlimited likes', 'Advanced filters', 'Boost once per week', 'See who liked you']
  },
  {
    tier: 'premium',
    priceMonthly: 39,
    features: ['All Plus features', 'Priority support', 'Travel mode', 'Incognito browsing']
  }
];

type SetState = (
  partial:
    | AppState
    | Partial<AppState>
    | ((state: AppState) => AppState | Partial<AppState>),
  replace?: boolean
) => void;

type GetState = () => AppState;

type ConnectQueueRefreshOptions = {
  count?: number;
  reason?: string;
  silent?: boolean;
};

type ConnectionLoadOptions = {
  limit?: number;
  silent?: boolean;
};

type ConnectLimitState = {
  remaining: number;
  max: number;
  resetsAtUtc: string;
  resetsAtLocal: string;
};

const DEFAULT_MAX_DAILY_CONNECTS = 3;
const DEFAULT_GENDER = 'Other';
const DEFAULT_SEXUALITY = 'Other';
const DEFAULT_LOOKING_FOR = 'Exploring';
const DEFAULT_ABOUT = '';

const toSafeOption = (value: string | null | undefined, allowed: readonly string[], fallback: string): string => {
  if (!value) {
    return fallback;
  }

  const match = allowed.find((option) => option.toLowerCase() === value.toLowerCase());
  return match ?? fallback;
};

const toSafeOptionListString = (value: string | null | undefined, allowed: readonly string[], fallback: string): string => {
  if (!value) {
    return fallback;
  }

  const normalized = value
    .split(/[,;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => allowed.find((option) => option.toLowerCase() === item.toLowerCase()))
    .filter((item): item is string => Boolean(item))
    .filter((item, index, items) => items.findIndex((candidate) => candidate.toLowerCase() === item.toLowerCase()) === index);

  return normalized.length > 0 ? normalized.join(', ') : fallback;
};

const toCanonicalPronoun = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const match = PRONOUN_OPTIONS.find((option) => option.toLowerCase() === value.trim().toLowerCase());
  return match ?? null;
};

function toSafeLookingForList(
  value: string | string[] | null | undefined,
  fallback?: string | string[] | null | undefined
): string[] {
  const parse = (source?: string | string[] | null): string[] => {
    const normalized = normalizeLookingFor(source);
    if (normalized.length === 0) {
      return [];
    }

    const allowed = new Set(LOOKING_FOR_OPTIONS.map((option) => option.toLowerCase()));
    const canonical = normalized
      .map((selection) => {
        const match = selection ? selection.trim().toLowerCase() : '';
        return allowed.has(match)
          ? LOOKING_FOR_OPTIONS.find((option) => option.toLowerCase() === match) ?? null
          : null;
      })
      .filter((option): option is (typeof LOOKING_FOR_OPTIONS)[number] => option !== null);

    if (canonical.length === 0) {
      return [];
    }

    return LOOKING_FOR_OPTIONS.filter((option) => canonical.some((item) => item.toLowerCase() === option.toLowerCase()));
  };

  const primary = parse(value);
  if (primary.length > 0) {
    return primary;
  }

  const secondary = parse(fallback);
  if (secondary.length > 0) {
    return secondary;
  }

  return [DEFAULT_LOOKING_FOR];
}

const normalizeLookingForSelections = (
  value: string | string[] | null | undefined,
  fallback?: string | string[] | null | undefined
): string[] => {
  const primary = normalizeLookingFor(value).filter((selection) => LOOKING_FOR_OPTIONS.some((option) => option.toLowerCase() === selection.toLowerCase()));
  if (primary.length > 0) {
    return LOOKING_FOR_OPTIONS.filter((option) => primary.some((item) => item.toLowerCase() === option.toLowerCase()));
  }

  const secondary = normalizeLookingFor(fallback).filter((selection) => LOOKING_FOR_OPTIONS.some((option) => option.toLowerCase() === selection.toLowerCase()));
  if (secondary.length > 0) {
    return LOOKING_FOR_OPTIONS.filter((option) => secondary.some((item) => item.toLowerCase() === option.toLowerCase()));
  }

  return [];
};

const nowIsoString = (): string => new Date().toISOString();

const createConnectLimitState = (
  partial: Partial<ConnectLimitState>,
  fallbackMax: number = DEFAULT_MAX_DAILY_CONNECTS
): ConnectLimitState => {
  const safeMax = partial.max ?? fallbackMax ?? DEFAULT_MAX_DAILY_CONNECTS;
  const fallbackTimestamp = nowIsoString();
  const resetsAtUtc = partial.resetsAtUtc ?? fallbackTimestamp;
  return {
    remaining: Math.max(0, partial.remaining ?? safeMax),
    max: safeMax,
    resetsAtUtc,
    resetsAtLocal: partial.resetsAtLocal ?? resetsAtUtc
  };
};

const mapConnectLimitDto = (dto: ConnectLimitStatusDto): ConnectLimitState =>
  createConnectLimitState(
    {
      remaining: dto.remaining,
      max: dto.max,
      resetsAtUtc: dto.resetsAtUtc,
      resetsAtLocal: dto.resetsAtLocal
    },
    dto.max
  );

const parseConnectLimitDetails = (
  details?: ApiErrorDetails | null,
  fallback?: ConnectLimitState | null
): ConnectLimitState | null => {
  if (!details) {
    return fallback ?? null;
  }

  const pickString = (key: string): string | undefined => {
    const value = details[key];
    if (!value || value.length === 0) {
      return undefined;
    }
    return value[0];
  };

  const pickNumber = (key: string): number | undefined => {
    const raw = pickString(key);
    if (!raw) {
      return undefined;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const extracted = {
    remaining: pickNumber('remaining'),
    max: pickNumber('max') ?? fallback?.max,
    resetsAtUtc: pickString('resetsAtUtc'),
    resetsAtLocal: pickString('resetsAtLocal')
  };

  const hasValue =
    extracted.remaining !== undefined ||
    extracted.max !== undefined ||
    typeof extracted.resetsAtUtc === 'string' ||
    typeof extracted.resetsAtLocal === 'string';

  if (!hasValue) {
    return fallback ?? null;
  }

  return createConnectLimitState(extracted, extracted.max ?? fallback?.max ?? DEFAULT_MAX_DAILY_CONNECTS);
};

let authInitializationPromise: Promise<void> | null = null;

const getConnectionViewKey = (connection: Pick<Connection, 'id' | 'matchedAtUtc'>): string =>
  `${connection.id}:${connection.matchedAtUtc ?? ''}`;

const creator = (set: SetState, get: GetState): AppState => {
  const clearSessionState = () =>
    set(() => {
      clearStoredAccessToken();
      return {
        authToken: null,
        user: null,
        pendingVerification: false,
        discoverQueue: [],
        unviewedConnectionCount: 0,
        connectLimit: null,
        isConnectLimitModalOpen: false
      };
    });

  const applyAuthenticatedSession = async (tokens: AuthTokensResponseDto) => {
    setStoredAccessToken(tokens.accessToken);
    let profile: ProfileDetailsDto | null = null;
    const profileResult = await getProfile(tokens.accessToken);

    if (profileResult.ok) {
      profile = profileResult.data;
    } else if (profileResult.status === 401) {
      throw new Error('Session expired');
    }

    const userProfile = mapApiUserProfile(tokens.user, profile);
    const previousUserId = get().user?.id;

    const updates: Partial<AppState> = {
      authToken: tokens.accessToken,
      user: userProfile,
      pendingVerification: derivePendingVerification(profile, tokens.user),
      lastSyncedAt: profile?.updatedAtUtc ?? tokens.user.updatedAtUtc,
      authInitialized: true
    };

    if (!previousUserId || previousUserId !== tokens.user.id) {
      updates.discoverQueue = [];
      updates.matches = [];
      updates.likedProfileIds = [];
      updates.dismissedProfileIds = [];
      updates.unviewedConnectionCount = 0;
    }

    set(updates);

    try {
      await syncConnectLimit(tokens.accessToken);
    } catch (error) {
      console.warn('Unable to synchronize connect limit status after authentication.', error);
    }

    // Prime matches silently so navigation can reflect connections availability without forcing a page visit.
    void get()
      .loadConnectiones?.({ silent: true })
      .catch((error) => console.warn('Unable to prefetch connections after authentication.', error));
  };

  const applyProfileRefresh = (profile: ProfileDetailsDto) => {
    set((state) => {
      if (!state.user) {
        return state;
      }

      const photos = (profile.photos ?? [])
        .map((photo) => normalizePhotoUrl(photo.url))
        .filter((url): url is string => Boolean(url));
      const photoRefs = (profile.photos ?? [])
        .map((photo) => ({
          id: photo.id,
          url: normalizePhotoUrl(photo.url) ?? photo.url,
          isApproved: photo.isApproved,
          isPendingReview: photo.isPendingReview,
          reviewError: photo.reviewError ?? null,
          uploadedAtUtc: photo.uploadedAtUtc,
          displayOrder: photo.displayOrder
        }))
        .filter((p) => Boolean(p.url));
      const updatedAt = profile.updatedAtUtc ?? nowIsoString();
      const pendingProfile = profile.pendingRevision?.proposedProfile;
      const hasPendingProfileChanges = Boolean(profile.pendingRevision?.hasProfileChanges);
      const effectiveSocial = pendingProfile?.social ?? profile.social;
      const isTestUser = profile.isTestUser ?? state.user.isTestUser ?? false;
      const subscription = isTestUser || profile.hasSubscription ? 'plus' : 'free';
      const effectiveLookingFor = pendingProfile ? pendingProfile.lookingFor : profile.lookingFor;
      const effectiveGender = pendingProfile ? pendingProfile.gender : profile.gender;
      const effectiveSexuality = pendingProfile ? pendingProfile.sexuality : profile.sexuality;
      const lookingFor = toSafeLookingForList(effectiveLookingFor, state.user.lookingFor);

      return {
        user: {
          ...state.user,
          displayName: pendingProfile?.firstName ?? profile.firstName ?? state.user.displayName,
          profileType: profile.profileType ?? state.user.profileType ?? 'Standard',
          eventDate: profile.eventDate ?? state.user.eventDate ?? null,
          eventTime: profile.eventTime ?? state.user.eventTime ?? null,
          profileLink: pendingProfile?.profileLink ?? profile.profileLink ?? state.user.profileLink ?? null,
          age: pendingProfile?.age ?? profile.age ?? state.user.age,
          location: getPreferredVenueAddress(pendingProfile?.venue ?? profile.venue, pendingProfile?.location || profile.location) || state.user.location,
          bio: pendingProfile?.aboutMe ?? profile.aboutMe ?? state.user.bio,
          gender: effectiveGender,
          sexuality: effectiveSexuality,
          pronouns: toCanonicalPronoun(pendingProfile?.pronouns ?? profile.pronouns ?? state.user.pronouns),
          lookingFor,
          photos,
          photoRefs,
          socialHandles: {
            platform: effectiveSocial?.platform ?? null,
            handle: effectiveSocial?.handle ?? null,
            signal: effectiveSocial?.signal ?? null,
            secondaryPlatform: effectiveSocial?.secondaryPlatform ?? null,
            secondaryHandle: effectiveSocial?.secondaryHandle ?? null
          },
          subscription,
          subscriptionStatus: profile.subscriptionStatus ?? state.user.subscriptionStatus ?? 'none',
          subscriptionPeriodEndUtc: profile.subscriptionCurrentPeriodEndUtc ?? state.user.subscriptionPeriodEndUtc ?? null,
          verified: profile.isApproved ?? state.user.verified ?? false,
          isApproved: profile.isApproved ?? state.user.isApproved,
          isActive: profile.isActive ?? state.user.isActive,
          isPaused: profile.isPaused ?? state.user.isPaused,
          marketingOptIn: profile.marketingOptIn ?? state.user.marketingOptIn ?? false,
          approvalEmailsOptIn: profile.approvalEmailsOptIn ?? state.user.approvalEmailsOptIn ?? false,
          connectionEmailsOptIn: profile.connectionEmailsOptIn ?? state.user.connectionEmailsOptIn ?? false,
          isVisibleToOthers: profile.isVisibleToOthers ?? state.user.isVisibleToOthers ?? false,
          isTestUser,
          hasPendingProfileChanges,
          pendingProfileSubmittedAtUtc: hasPendingProfileChanges
            ? profile.pendingRevision?.requestedAtUtc ?? state.user.pendingProfileSubmittedAtUtc ?? null
            : null,
          updatedAtUtc: updatedAt
        },
        pendingVerification: profile.isApproved === false,
        lastSyncedAt: updatedAt
      };
    });
  };

  const syncConnectLimit = async (tokenOverride?: string | null) => {
    const token = typeof tokenOverride === 'undefined' ? get().authToken : tokenOverride;
    if (!token) {
      return;
    }

    const result = await getConnectLimitStatus(token);
    if (!result.ok) {
      return;
    }

    set({ connectLimit: mapConnectLimitDto(result.data) });
  };

  return {
    user: null,
    discoverQueue: [],
    matches: [],
    unviewedConnectionCount: 0,
    viewedConnectionKeysByUser: {},
    likedProfileIds: [],
    dismissedProfileIds: [],
    availablePlans: subscriptionPlans,
    pendingVerification: false,
    notifications: defaultNotifications,
    isLoadingConnectiones: false,
    isRefreshingQueue: false,
    connectionStatus: 'idle',
    onlineUsers: {},
    lastPresenceSync: undefined,
    lastSyncedAt: undefined,
    authToken: null,
    authInitialized: false,
    language: 'da',
    layoutMode: 'mobile',
    queueRefreshReason: null,
    matchCelebration: null,
    connectLimit: null,
    isConnectLimitModalOpen: false,
    setUser: (user: UserProfile) => set({ user, lastSyncedAt: new Date().toISOString() }),
    restoreDefaultUser: () => set({ user: cloneUser(baseUserTemplate), lastSyncedAt: baseUserTemplate.updatedAtUtc }),
    updateUser: (update: Partial<UserProfile>) =>
    set((state) => {
      if (!state.user) {
        return state;
      }

      const updatedAt = update.updatedAtUtc ?? new Date().toISOString();
      return {
        user: {
          ...state.user,
          ...update,
          photos: update.photos ?? state.user.photos,
          privacy: update.privacy ?? state.user.privacy,
          notifications: update.notifications ?? state.user.notifications,
          updatedAtUtc: updatedAt
        },
        lastSyncedAt: updatedAt
      };
    }),
  detectAndUpdateLocation: async () => {
    const token = get().authToken;

    if (!token) {
      return { success: false, errorCode: 'auth_required' };
    }

    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return { success: false, errorCode: 'unsupported' };
    }

    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 30000
      });
    }).catch((error: GeolocationPositionError) => {
      const reasonCode =
        error.code === error.PERMISSION_DENIED
          ? 'permission_denied'
          : error.code === error.POSITION_UNAVAILABLE
            ? 'position_unavailable'
            : 'timed_out';
      return Promise.reject(new Error(reasonCode));
    });

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;

    const updateResult = await updateLocation(token, latitude, longitude);
    if (!updateResult.ok) {
      return { success: false, error: updateResult.error ?? 'Unable to update location.' };
    }

    const label = updateResult.data?.locationLabel?.trim() || null;

    set((state) => {
      if (!state.user) {
        return state;
      }

      return {
        user: {
          ...state.user,
          location: label ?? state.user.location,
          updatedAtUtc: nowIsoString()
        }
      };
    });

    if (canUseDiscover(get().user?.profileType) && !get().user?.isPaused) {
      void get().refreshConnectQueue({ silent: true, reason: 'location-update' });
    }

    return { success: true, label: label ?? undefined };
  },
  saveProfile: async (payload: SaveProfilePayload) => {
    const token = get().authToken;
    if (!token) {
      return { success: false, message: 'Not authenticated.' };
    }

    const currentUser = get().user;
    const aboutMeRaw = payload.aboutMe ?? currentUser?.bio ?? DEFAULT_ABOUT;
    if (aboutMeRaw.trim().length > 15000) {
      const message = 'About/bio must be 15,000 characters or fewer.';
      console.warn(message);
      return { success: false, message };
    }

    const aboutMe = aboutMeRaw.slice(0, 15000);

    const location = (payload.location ?? currentUser?.location ?? '').trim().slice(0, 128);
    const venue = payload.venue ?? currentUser?.venue ?? null;

    const gender = payload.gender === null
      ? null
      : toSafeOption(payload.gender ?? currentUser?.gender, GENDER_OPTIONS, DEFAULT_GENDER);
    const sexuality = payload.sexuality === null
      ? null
      : toSafeOptionListString(payload.sexuality ?? currentUser?.sexuality, SEXUALITY_OPTIONS, DEFAULT_SEXUALITY);
    const pronouns = payload.pronouns === null
      ? null
      : toCanonicalPronoun(payload.pronouns ?? currentUser?.pronouns);
    const lookingForSelections = payload.lookingFor !== undefined
      ? normalizeLookingForSelections(payload.lookingFor)
      : normalizeLookingForSelections(undefined, currentUser?.lookingFor);
    const lookingFor = lookingForSelections.length ? lookingForSelections.join(', ') : null;

    const age = payload.age ?? currentUser?.age ?? 25;

    const socialPlatform = (payload.socialPlatform ?? currentUser?.socialHandles?.platform ?? '').trim().slice(0, 16);
    const socialHandle = (payload.socialHandle ?? currentUser?.socialHandles?.handle ?? '').trim().slice(0, 64);
    const hasCompleteSocial = Boolean(socialPlatform && socialHandle);
    const normalizedPlatform = hasCompleteSocial ? socialPlatform : null;
    const normalizedHandle = hasCompleteSocial ? socialHandle : null;
    const secondarySocialPlatform = (payload.secondarySocialPlatform ?? currentUser?.socialHandles?.secondaryPlatform ?? '').trim().slice(0, 64);
    const secondarySocialHandle = (payload.secondarySocialHandle ?? currentUser?.socialHandles?.secondaryHandle ?? '').trim().slice(0, 64);
    const hasCompleteSecondarySocial = Boolean(secondarySocialPlatform && secondarySocialHandle);
    const normalizedSecondaryPlatform = hasCompleteSecondarySocial ? secondarySocialPlatform : null;
    const normalizedSecondaryHandle = hasCompleteSecondarySocial ? secondarySocialHandle : null;

    const request: UpdateProfileRequestDto = {
      aboutMe,
      firstName: payload.firstName ?? currentUser?.displayName ?? 'Member',
      age: age < 18 ? 18 : age > 120 ? 120 : age,
      gender,
      sexuality,
      pronouns,
      newEvent: payload.newEvent,
      eventDate: payload.eventDate !== undefined ? payload.eventDate : currentUser?.profileType === 'Event' ? null : currentUser?.eventDate ?? null,
      eventTime: payload.eventTime !== undefined ? payload.eventTime : currentUser?.profileType === 'Event' ? null : currentUser?.eventTime ?? null,
      profileLink: payload.profileLink ?? currentUser?.profileLink ?? null,
      lookingFor,
      location,
      venue,
      socialHandles: {
        platform: normalizedPlatform,
        handle: normalizedHandle,
        signal: null,
        secondaryPlatform: normalizedSecondaryPlatform,
        secondaryHandle: normalizedSecondaryHandle
      },
      marketingOptIn: payload.marketingOptIn ?? currentUser?.marketingOptIn ?? false,
      approvalEmailsOptIn: payload.approvalEmailsOptIn ?? currentUser?.approvalEmailsOptIn ?? false,
      connectionEmailsOptIn: payload.connectionEmailsOptIn ?? currentUser?.connectionEmailsOptIn ?? false,
      isVisibleToOthers: payload.isVisibleToOthers ?? currentUser?.isVisibleToOthers ?? false
    };

    const result = await updateProfile(token, request);
    if (!result.ok) {
      const message = result.details?.validationErrors?.[0] ?? result.error ?? 'Unable to update profile.';
      console.warn('Profile save failed', {
        status: result.status,
        code: result.code,
        error: message,
        details: result.details
      });
      return { success: false, message };
    }
    const refreshed = await getProfile(token);
    if (refreshed.ok) {
      applyProfileRefresh(refreshed.data);
    } else {
      set((state) => {
        if (!state.user) {
          return state;
        }

        const updatedAt = nowIsoString();
        return {
          user: {
            ...state.user,
            hasPendingProfileChanges: true,
            pendingProfileSubmittedAtUtc: state.user.pendingProfileSubmittedAtUtc ?? updatedAt,
            updatedAtUtc: updatedAt
          },
          pendingVerification: state.pendingVerification,
          lastSyncedAt: updatedAt
        };
      });
    }

    return { success: true };
  },
  addPhoto: (photoUrl: string) =>
    set((state) => {
      if (!state.user) {
        return state;
      }

      const updatedAt = new Date().toISOString();
      return {
        user: {
          ...state.user,
          photos: Array.from(new Set([...state.user.photos, photoUrl])),
          updatedAtUtc: updatedAt
        },
        lastSyncedAt: updatedAt
      };
    }),
  removePhoto: (photoUrl: string) =>
    set((state) => {
      if (!state.user) {
        return state;
      }

      const updatedAt = new Date().toISOString();
      return {
        user: {
          ...state.user,
          photos: state.user.photos.filter((photo: string) => photo !== photoUrl),
          updatedAtUtc: updatedAt
        },
        lastSyncedAt: updatedAt
      };
    }),
  uploadProfilePhotos: async (files: File[]) => {
    const token = get().authToken;
    if (!token || !files || files.length === 0) {
      return { success: false, error: 'No photos selected.' };
    }

    const result = await uploadProfilePhotos(token, files);
    if (!result.ok) {
      console.warn('Photo upload failed', { status: result.status, code: result.code, error: result.error });
      return { success: false, error: result.error, status: result.status, code: result.code };
    }

    const refreshed = await getProfile(token);
    if (refreshed.ok) {
      applyProfileRefresh(refreshed.data);
    }

    return { success: true };
  },
  deleteProfilePhoto: async (photoId: string) => {
    const token = get().authToken;
    if (!token) {
      return false;
    }

    const result = await deleteProfilePhoto(token, photoId);
    if (!result.ok) {
      console.warn('Photo delete failed', { status: result.status, code: result.code, error: result.error });
      return false;
    }

    const refreshed = await getProfile(token);
    if (refreshed.ok) {
      applyProfileRefresh(refreshed.data);
    }

    return true;
  },
  reorderProfilePhotos: async (photoIds: string[]) => {
    const token = get().authToken;
    if (!token) {
      return { success: false, error: 'You need to be signed in to reorder photos.' };
    }

    const result = await reorderProfilePhotos(token, photoIds);
    if (!result.ok) {
      console.warn('Photo reorder failed', { status: result.status, code: result.code, error: result.error });
      return { success: false, error: result.error, status: result.status, code: result.code };
    }

    const refreshed = await getProfile(token);
    if (refreshed.ok) {
      applyProfileRefresh(refreshed.data);
    }

    return { success: true };
  },
  refreshUserProfile: async () => {
    const token = get().authToken;
    if (!token) {
      return false;
    }

    const refreshed = await getProfile(token);
    if (!refreshed.ok) {
      console.warn('Profile refresh failed', { status: refreshed.status, code: refreshed.code });
      return false;
    }

    applyProfileRefresh(refreshed.data);
    return true;
  },
  pauseAccount: async () => {
    const token = get().authToken;

    if (!token) {
      return { success: false, error: 'You need to be signed in to pause your profile.' };
    }

    const previousUser = get().user;
    const previousQueue = get().discoverQueue;
    const optimisticAt = nowIsoString();

    set((state) =>
      state.user
        ? {
            user: {
              ...state.user,
              isPaused: true,
              isActive: false,
              updatedAtUtc: optimisticAt
            },
            lastSyncedAt: optimisticAt,
            discoverQueue: [],
            isRefreshingQueue: false,
            queueRefreshReason: null
          }
        : state
    );

    const result = await apiPauseProfile(token);

    if (!result.ok) {
      set({
        user: previousUser ?? null,
        discoverQueue: previousQueue,
        isRefreshingQueue: false,
        queueRefreshReason: null,
        lastSyncedAt: previousUser?.updatedAtUtc
      });
      return { success: false, error: result.error, code: result.code, status: result.status };
    }

    return { success: true };
  },
  resumeAccount: async () => {
    const token = get().authToken;

    if (!token) {
      return { success: false, error: 'You need to be signed in to resume your profile.' };
    }

    const result = await apiResumeProfile(token);

    if (!result.ok) {
      return { success: false, error: result.error, code: result.code, status: result.status, details: result.details };
    }

    const refreshed = await getProfile(token);
    if (refreshed.ok) {
      applyProfileRefresh(refreshed.data);
    } else {
      const updatedAt = nowIsoString();
      set((state) =>
        state.user
          ? {
              user: {
                ...state.user,
                isPaused: false,
                isActive: true,
                updatedAtUtc: updatedAt
              },
              lastSyncedAt: updatedAt
            }
          : state
      );
    }

    void get().refreshConnectQueue({ reason: 'resume' });
    return { success: true };
  },
  recordConnectionDecision: async (id: string, decision: 'like' | 'pass') => {
    if (!canUseDiscover(get().user?.profileType)) {
      set({ discoverQueue: [] });
      return;
    }

    const candidate = get().discoverQueue.find((match) => match.id === id);

    if (!candidate) {
      return;
    }

    set((state) => ({
      discoverQueue: state.discoverQueue.filter((match) => match.id !== id)
    }));

    const token = get().authToken;
    if (!token) {
      set((state) => ({
        discoverQueue: [candidate, ...state.discoverQueue]
      }));
      console.warn('Cannot submit connect decision without an access token.');
      return;
    }

    const payload: ConnectRequestDto = {
      candidateId: id,
      isLike: decision === 'like'
    };

    try {
      const result = await submitConnectAction(token, payload);
      if (!result.ok) {
        if (result.status === 429 && result.code === 'connect_limit_reached') {
          set((state) => ({
            discoverQueue: [candidate, ...state.discoverQueue]
          }));
          return;
        }

        throw new Error(result.error ?? 'Failed to submit connect decision');
      }

      const likedProfile = decision === 'like' ? { ...cloneConnection(candidate), isNew: true } : null;
      const isMutualConnection = decision === 'like' && Boolean(result.data?.isMutualConnection);

      set((state) => {
        const updates: Partial<AppState> = {};

        if (decision === 'like') {
          updates.likedProfileIds = Array.from(new Set([...state.likedProfileIds, id]));
          updates.dismissedProfileIds = state.dismissedProfileIds.filter((dismissedId) => dismissedId !== id);

          if (isMutualConnection && likedProfile) {
            const existingIndex = state.matches.findIndex((match) => match.id === id);
            const nextConnectiones = existingIndex >= 0
              ? state.matches.map((match, index) => (index === existingIndex ? { ...likedProfile } : match))
              : [likedProfile, ...state.matches];

            updates.matches = nextConnectiones;
            updates.unviewedConnectionCount = nextConnectiones.filter((match) => match.isNew).length;
          }
        } else {
          updates.dismissedProfileIds = Array.from(new Set([...state.dismissedProfileIds, id]));
          updates.likedProfileIds = state.likedProfileIds.filter((likedId) => likedId !== id);
        }

        // An overlapping queue refresh may have completed after the optimistic removal.
        updates.discoverQueue = state.discoverQueue.filter((match) => match.id !== id);

        return updates;
      });

      if (isMutualConnection && likedProfile) {
        get().triggerConnectionCelebration(likedProfile);
      }

      void get().refreshConnectLimit();
    } catch (error) {
      set((state) => ({
        discoverQueue: [candidate, ...state.discoverQueue]
      }));
      console.error('Failed to submit connect decision', error);
    } finally {
      const remaining = get().discoverQueue.length;
      if (remaining < 3) {
        void get().refreshConnectQueue({ silent: true, reason: 'auto-top-up' });
      }
    }
  },
  markConnectionAsViewed: (id: string) =>
    set((state) => {
      const updatedConnectiones = state.matches.map((match: Connection) =>
        match.id === id || match.matchId === id
          ? {
              ...match,
              isNew: false
            }
          : match
      );
      const unread = updatedConnectiones.filter((match) => match.isNew).length;
      const currentUserId = state.user?.id;
      if (!currentUserId) {
        return { matches: updatedConnectiones, unviewedConnectionCount: unread };
      }

      const viewedKeys = new Set(state.viewedConnectionKeysByUser[currentUserId] ?? []);
      updatedConnectiones.forEach((match) => {
        if ((match.id === id || match.matchId === id) && !match.isNew) {
          viewedKeys.add(getConnectionViewKey(match));
        }
      });

      return {
        matches: updatedConnectiones,
        unviewedConnectionCount: unread,
        viewedConnectionKeysByUser: {
          ...state.viewedConnectionKeysByUser,
          [currentUserId]: Array.from(viewedKeys)
        }
      };
    }),
  setSubscriptionTier: (tier: SubscriptionTier) =>
    set((state) =>
      state.user
        ? (() => {
            const updatedAt = new Date().toISOString();
            return {
              user: {
                ...state.user,
                subscription: tier,
                updatedAtUtc: updatedAt
              },
              lastSyncedAt: updatedAt
            };
          })()
        : state
    ),
  setPendingVerification: (pending: boolean) => set({ pendingVerification: pending }),
  dismissNotification: (id: string) =>
    set((state) => ({
      notifications: state.notifications.map((notification) =>
        notification.id === id
          ? {
              ...notification,
              isNew: false
            }
          : notification
      )
    })),
  pushNotification: (notification: ConnectionNotification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications].slice(0, 10)
    })),
  clearNotifications: () => set({ notifications: [] }),
  setIsLoadingConnectiones: (pending: boolean) => set({ isLoadingConnectiones: pending }),
  setConnectionStatus: (status: ConnectionStatus) => set({ connectionStatus: status }),
  setOnlineUsers: (snapshot: PresenceSnapshot) =>
    set((state) => {
      const nextOnline: Record<string, OnlineUserState> = {};
      snapshot.users.forEach((user) => {
        nextOnline[user.userId] = {
          isOnline: user.isOnline,
          lastSeenUtc: user.lastSeenUtc
        };
      });

      const updatedConnectiones = state.matches.map((match) => {
        const presence = nextOnline[match.id];
        if (presence) {
          return {
            ...match,
            isOnline: presence.isOnline,
            lastSeenUtc: presence.lastSeenUtc ?? match.lastSeenUtc
          };
        }

        if (state.onlineUsers[match.id]?.isOnline) {
          return {
            ...match,
            isOnline: false,
            lastSeenUtc: state.onlineUsers[match.id]?.lastSeenUtc ?? match.lastSeenUtc
          };
        }

        return match.isOnline ? { ...match, isOnline: false } : match;
      });

      return {
        onlineUsers: nextOnline,
        matches: updatedConnectiones,
        lastPresenceSync: snapshot.generatedAtUtc
      };
    }),
  updatePresence: (change: PresenceChanged) =>
    set((state) => {
      const nextOnline: Record<string, OnlineUserState> = {
        ...state.onlineUsers,
        [change.userId]: {
          isOnline: change.isOnline,
          lastSeenUtc: change.changedAtUtc
        }
      };

      const updatedConnectiones = state.matches.map((match) =>
        match.id === change.userId
          ? {
              ...match,
              isOnline: change.isOnline,
              lastSeenUtc: change.changedAtUtc
            }
          : match
      );

      return {
        onlineUsers: nextOnline,
        matches: updatedConnectiones,
        lastPresenceSync: change.changedAtUtc
      };
    }),
  applyProfileSnapshot: (snapshot: ProfileSnapshotPayload) =>
    set((state) => {
      const normalizedPhotos = snapshot.photoUrls
        .map((url) => normalizePhotoUrl(url))
        .filter((url): url is string => Boolean(url));

      const isCurrentUser = state.user?.id === snapshot.userId;
      const updatedUser = isCurrentUser
        ? {
            ...state.user!,
            displayName: snapshot.displayName,
            age: snapshot.age,
            bio: snapshot.bio,
            pronouns: snapshot.pronouns ?? state.user!.pronouns ?? null,
            photos: normalizedPhotos.length > 0 ? normalizedPhotos : state.user!.photos,
            isActive: snapshot.isActive,
            isPaused: snapshot.isPaused,
            updatedAtUtc: snapshot.updatedAtUtc
          }
        : state.user;

      const updatedConnectiones = state.matches.map((match) =>
        match.id === snapshot.userId
          ? {
              ...match,
              name: snapshot.displayName,
              age: snapshot.age,
              bio: snapshot.bio,
              pronouns: snapshot.pronouns ?? match.pronouns ?? null,
              image: normalizedPhotos[0] ?? match.image
            }
          : match
      );

      return {
        user: updatedUser,
        matches: updatedConnectiones,
        lastSyncedAt: snapshot.updatedAtUtc
      };
    }),
  applyAdminApprovalUpdate: (update: AdminApprovalPayload) =>
    set((state) => {
      if (state.user?.id !== update.userId) {
        return {};
      }

      return {
        user: state.user
          ? {
              ...state.user,
              isApproved: update.isApproved,
              updatedAtUtc: update.occurredAtUtc,
              isPaused: !update.isApproved,
              hasPendingProfileChanges: false,
              pendingProfileSubmittedAtUtc: null
            }
          : null,
        pendingVerification: !update.isApproved,
        lastSyncedAt: update.occurredAtUtc
      };
    }),
  handleConnectLimitExceeded: (details?: ApiErrorDetails | null) =>
    set((state) => {
      const fallback = state.connectLimit ?? createConnectLimitState({ remaining: 0 }, DEFAULT_MAX_DAILY_CONNECTS);
      const parsed = parseConnectLimitDetails(details, fallback) ?? fallback;
      const next = createConnectLimitState(
        {
          remaining: 0,
          max: parsed.max,
          resetsAtUtc: parsed.resetsAtUtc,
          resetsAtLocal: parsed.resetsAtLocal
        },
        parsed.max
      );

      return {
        connectLimit: next,
        isConnectLimitModalOpen: true
      };
    }),
  applyConnectLimitReset: (payload: ConnectLimitResetPayload) =>
    set((state) => {
      const connectLimit = createConnectLimitState(
        {
          remaining: payload.maxDailyConnects,
          max: payload.maxDailyConnects,
          resetsAtUtc: payload.nextResetUtc,
          resetsAtLocal: payload.nextResetUtc
        },
        payload.maxDailyConnects
      );

      const notificationTime = new Date(payload.resetOccurredAtUtc).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });

      const notification: ConnectionNotification = {
        id: `connect-limit-reset-${payload.resetOccurredAtUtc}`,
        title: 'Daily connects reset',
        message: payload.message,
        tone: 'info',
        receivedAt: notificationTime,
        isNew: true
      };

      return {
        connectLimit,
        isConnectLimitModalOpen: false,
        notifications: [notification, ...state.notifications].slice(0, 10)
      };
    }),
  setAuthToken: (token: string | null) => set({ authToken: token }),
  setLanguage: (language: SupportedLanguage) => {
    set({ language });

    const token = get().authToken;
    if (!token) {
      return;
    }

    void updatePreferredLanguage(token, language).then((result) => {
      if (!result.ok) {
        console.warn('Failed to persist language preference on server.', result.error);
      }
    });
  },
  setLayoutMode: (mode: LayoutMode) => set({ layoutMode: mode }),
  triggerConnectionCelebration: (match: Connection) => set({ matchCelebration: match }),
  dismissConnectionCelebration: () => set({ matchCelebration: null }),
  refreshConnectQueue: async (options: ConnectQueueRefreshOptions = {}) => {
    const { count = 10, reason = 'manual', silent = false } = options;
    const token = get().authToken ?? undefined;

    if (!token) {
      return;
    }

    if (!canUseDiscover(get().user?.profileType)) {
      set((state) => ({
        discoverQueue: [],
        ...(silent ? {} : { isRefreshingQueue: false, queueRefreshReason: null })
      }));
      return;
    }

    if (get().user?.isPaused) {
      set((state) => ({
        discoverQueue: [],
        ...(silent ? {} : { isRefreshingQueue: false, queueRefreshReason: null })
      }));
      return;
    }

    if (!silent) {
      set({ isRefreshingQueue: true, queueRefreshReason: reason });
    }

    try {
      const result = await getConnectQueue(count, token);

      if (!result.ok) {
        console.error('Failed to refresh connect queue', result.error);
        return;
      }

      const profiles = result.data?.profiles ?? [];
      const mappedQueue = profiles.map((profile) => mapConnectProfileToConnection(profile));
      set((state) => {
        const handledProfileIds = new Set([...state.likedProfileIds, ...state.dismissedProfileIds]);
        return {
          discoverQueue: mappedQueue.filter((profile) => !handledProfileIds.has(profile.id))
        };
      });

      // Always refresh connect limit so remaining/next-reset stays in sync with the queue state.
      try {
        await syncConnectLimit(token);
      } catch (error) {
        console.warn('Failed to sync connect limit after queue refresh', error);
      }
    } catch (error) {
      console.error('Failed to refresh connect queue', error);
    } finally {
      if (!silent) {
        set({ isRefreshingQueue: false, queueRefreshReason: null });
      }
    }
  },
  refreshConnectLimit: async () => {
    try {
      await syncConnectLimit();
    } catch (error) {
      console.error('Failed to refresh connect limit status', error);
    }
  },
  loadConnectiones: async (options: ConnectionLoadOptions = {}) => {
    const token = get().authToken ?? undefined;
    if (!token) {
      return 'Authentication required.';
    }

    const { silent = false } = options;
    if (!silent) {
      set({ isLoadingConnectiones: true });
    }

    try {
      const result = await getConnectiones(token);
      const loadConnectionsMessage = 'failed to load connections.';

      if (!result.ok) {
        console.error('Failed to load connections', result.error);
        return `${loadConnectionsMessage} ${result.error}`;
      }

      const items = result.data?.connections ?? [];
      const mappedConnectiones = items.map((item) => mapConnectionListItemToConnection(item));
      const currentUserId = get().user?.id;
      const viewedKeys = new Set(currentUserId ? get().viewedConnectionKeysByUser[currentUserId] ?? [] : []);

      // Preserve locally viewed state when the backend still reports isNew=true
      const currentConnectiones = get().matches;
      const currentByKey = new Map<string, Connection>();
      currentConnectiones.forEach((match) => {
        currentByKey.set(match.id, match);
        if (match.matchId) {
          currentByKey.set(match.matchId, match);
        }
      });

      const mergedConnectiones = mappedConnectiones.map((match) => {
        const existing = currentByKey.get(match.id) ?? (match.matchId ? currentByKey.get(match.matchId) : undefined);
        if (existing && existing.isNew === false && match.isNew === true) {
          return { ...match, isNew: false };
        }
        if (match.isNew && viewedKeys.has(getConnectionViewKey(match))) {
          return { ...match, isNew: false };
        }
        return match;
      });

      const unread = mergedConnectiones.filter((match) => match.isNew).length;

      set({ matches: mergedConnectiones, unviewedConnectionCount: unread });
      return null;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'failed to load connections.';
      console.error('Failed to load connections', error);
      return message;
    } finally {
      if (!silent) {
        set({ isLoadingConnectiones: false });
      }
    }
  },
  unmatch: async (matchId: string) => {
    const token = get().authToken ?? undefined;
    if (!token) {
      console.warn('Cannot unmatch without an access token.');
      return false;
    }

    try {
      const result = await deleteConnection(matchId, token);
      if (!result.ok) {
        console.error('Failed to unmatch', result.error);
        return false;
      }

      set((state) => {
        const remaining = state.matches.filter((match) => match.matchId !== matchId && match.id !== matchId);
        const unread = remaining.filter((match) => match.isNew).length;
        return { matches: remaining, unviewedConnectionCount: unread };
      });

      return true;
    } catch (error) {
      console.error('Failed to unmatch', error);
      return false;
    }
  },
  authenticate: async (email: string, password: string, rememberMe = false) => {
    const result = await apiLogin({ email, password, rememberMe });

    if (!result.ok) {
      return { success: false, error: result.error, code: result.code, status: result.status, details: result.details };
    }

    try {
      await applyAuthenticatedSession(result.data);
      return { success: true };
    } catch (error) {
      clearSessionState();
      const message = error instanceof Error ? error.message : 'Unable to sign in right now.';
      return { success: false, error: message };
    }
  },
  upsertRegistrationDraft: async (payload: RegistrationDraftPayload) => {
    const result = await apiUpsertRegistrationDraft(payload);

    if (!result.ok) {
      const error = result.error || 'Unable to save your registration details right now.';
      return { success: false, error, code: result.code, status: result.status };
    }

    if (!result.data?.draftId) {
      return { success: false, error: 'Unable to save your registration details right now.' };
    }

    return {
      success: true,
      draftId: result.data.draftId,
      emailConfirmed: result.data.emailConfirmed,
      status: result.data.status ?? null
    };
  },
  finalizeRegistration: async (draftId: string) => {
    const result = await apiFinalizeRegistrationDraft(draftId);

    if (!result.ok) {
      return { success: false, error: result.error, code: result.code, status: result.status, details: result.details };
    }

    // Keep the user logged out until admin approval clears the account.
    clearSessionState();
    return { success: true };
  },
  registerAccount: async (payload: RegisterPayload) => {
    const result = await apiRegister(payload);

    if (!result.ok) {
      return { success: false, error: result.error, code: result.code, status: result.status };
    }

    // Avoid logging the user in on registration; account remains pending until approved.
    clearSessionState();
    return { success: true };
  },
  initializeAuth: async () => {
    if (get().authInitialized) {
      return;
    }

    if (authInitializationPromise) {
      await authInitializationPromise;
      return;
    }

    authInitializationPromise = (async () => {
      const refreshResult = await refreshSession();

      if (!refreshResult.ok) {
        clearSessionState();
        set({ authInitialized: true });
        return;
      }

      try {
        await applyAuthenticatedSession(refreshResult.data);
      } catch {
        clearSessionState();
        set({ authInitialized: true });
      }
    })();

    try {
      await authInitializationPromise;
    } finally {
      authInitializationPromise = null;
    }
  },
  deleteAccount: async () => {
    const token = get().authToken;

    if (!token) {
      return { success: false, error: 'You need to be signed in to delete your account.' };
    }

    const result = await deleteProfile(token, true);

    if (!result.ok) {
      const error = result.error ?? 'Unable to delete your account right now. Please try again.';
      return { success: false, error };
    }

    await get().logout();
    return { success: true };
  },
  logout: async () => {
    const token = get().authToken;

    try {
      await revokeSession(token ?? undefined);
    } catch {
      /* ignore revoke errors */
    }

    set({
      user: null,
      notifications: [],
      pendingVerification: false,
      authToken: null,
      isLoadingConnectiones: false,
      isRefreshingQueue: false,
      queueRefreshReason: null,
      connectionStatus: 'idle',
      onlineUsers: {},
      lastPresenceSync: undefined,
      lastSyncedAt: undefined,
      matches: [],
      unviewedConnectionCount: 0,
      discoverQueue: [],
      likedProfileIds: [],
      dismissedProfileIds: [],
      authInitialized: true,
      connectLimit: null,
      isConnectLimitModalOpen: false
    });
    clearStoredAccessToken();
  },
  setConnectLimitModalOpen: (open: boolean) => set({ isConnectLimitModalOpen: open })
  };
};

export const useAppStore = create<AppState>()(
  persist((set, get) => creator(set, get), {
    name: 'enkel-app-state',
    storage: createJSONStorage(() => localStorage),
    merge: (persistedState, currentState) => {
      const incoming = persistedState as Partial<AppState> | undefined;

      if (!incoming) {
        return currentState;
      }

      const normalizePersistedConnection = (match: any): Connection => ({
        ...match,
        lookingFor: normalizeLookingFor(match?.lookingFor)
      });

      const normalizedQueue = Array.isArray(incoming.discoverQueue)
        ? incoming.discoverQueue.map((match) => normalizePersistedConnection(match))
        : currentState.discoverQueue;

      const normalizedConnectiones = Array.isArray(incoming.matches)
        ? incoming.matches.map((match) => normalizePersistedConnection(match))
        : currentState.matches;

      const persistedViewedConnectionKeysByUser = incoming.viewedConnectionKeysByUser ?? currentState.viewedConnectionKeysByUser;
      const persistedUnviewedConnectionCount = typeof incoming.unviewedConnectionCount === 'number'
        ? incoming.unviewedConnectionCount
        : normalizedConnectiones.filter((match) => match.isNew).length;

      return {
        ...currentState,
        discoverQueue: normalizedQueue,
        matches: normalizedConnectiones,
        unviewedConnectionCount: persistedUnviewedConnectionCount,
        viewedConnectionKeysByUser: persistedViewedConnectionKeysByUser,
        notifications: incoming.notifications ?? currentState.notifications,
        likedProfileIds: incoming.likedProfileIds ?? currentState.likedProfileIds,
        dismissedProfileIds: incoming.dismissedProfileIds ?? currentState.dismissedProfileIds,
        language: incoming.language ?? currentState.language,
        layoutMode: (incoming as any).layoutMode ?? currentState.layoutMode
      };
    },
    partialize: (state) =>
      ({
        discoverQueue: state.discoverQueue,
        matches: state.matches,
        unviewedConnectionCount: state.unviewedConnectionCount,
        viewedConnectionKeysByUser: state.viewedConnectionKeysByUser,
        notifications: state.notifications,
        likedProfileIds: state.likedProfileIds,
        dismissedProfileIds: state.dismissedProfileIds,
        language: state.language,
        layoutMode: state.layoutMode
      } as Partial<AppState>)
  })
);
